/**
 * Auditor Application Queue Page
 *
 * Display list of applications assigned to auditor for review with:
 * - Search and filtering capabilities
 * - Status and priority filters
 * - Application queue table
 * - Quick actions
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { Table } from '../../components/common/Table';
import { formatDate } from '../../utils/formatting';
import { mockApi } from '../../services/mockApi';
import type { Application } from '../../types/application';

export const AuditorApplicationQueuePage: React.FC = () => {
  const { t } = useTranslation(['common', 'auditor']);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<Application[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const response = await mockApi.getAllApplications();
      if (response.success && response.data) {
        // Filter to only applications for auditor review
        const auditorApplications = response.data.filter(a =>
          ['SUBMITTED', 'UNDER_REVIEW', 'AUDIT_SCHEDULED', 'AUDIT_IN_PROGRESS', 'APPROVED', 'REJECTED'].includes(a.status)
        );
        setApplications(auditorApplications);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          app.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const paginatedApplications = filteredApplications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'AUDIT_SCHEDULED':
      case 'AUDIT_IN_PROGRESS':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'UNDER_REVIEW':
      case 'SUBMITTED':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPriorityBadge = (completenessScore: number): { label: string; variant: 'success' | 'warning' | 'error' } => {
    if (completenessScore >= 80) {
      return { label: t('auditor:applications.low'), variant: 'success' };
    } else if (completenessScore >= 60) {
      return { label: t('auditor:applications.medium'), variant: 'warning' };
    } else {
      return { label: t('auditor:applications.high'), variant: 'error' };
    }
  };

  const handleViewApplication = (id: string) => {
    window.location.href = `/auditor/applications/${id}`;
  };

  const handleScheduleAudit = (id: string) => {
    alert(`Schedule audit for application: ${id}`);
  };

  const columns = [
    {
      key: 'companyName',
      header: t('auditor:applications.companyName'),
      render: (app: Application) => (
        <div>
          <div className="font-medium text-gray-800">{app.companyName}</div>
          <div className="text-sm text-gray-500">{app.type}</div>
        </div>
      ),
    },
    {
      key: 'submittedDate',
      header: t('auditor:applications.submittedDate'),
      render: (app: Application) => (
        <span className="text-sm text-gray-700">{formatDate(app.submittedAt || app.createdAt)}</span>
      ),
    },
    {
      key: 'status',
      header: t('auditor:applications.status'),
      render: (app: Application) => (
        <Badge variant={getStatusVariant(app.status)}>
          {app.status.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'priority',
      header: t('auditor:applications.priority'),
      render: (app: Application) => {
        const priority = getPriorityBadge(app.completenessScore);
        return (
          <Badge variant={priority.variant}>
            {priority.label}
          </Badge>
        );
      },
    },
    {
      key: 'lastActivity',
      header: t('auditor:applications.lastActivity'),
      render: (app: Application) => (
        <span className="text-sm text-gray-700">{formatDate(app.updatedAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: t('common:common.details'),
      render: (app: Application) => (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleViewApplication(app.id)}>
            {t('common:actions.view')}
          </Button>
          {app.status === 'UNDER_REVIEW' && (
            <Button size="sm" onClick={() => handleScheduleAudit(app.id)}>
              {t('auditor:actions.scheduleAudit')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: 'ALL', label: t('auditor:applications.allStatuses') },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'UNDER_REVIEW', label: 'Under Review' },
    { value: 'AUDIT_SCHEDULED', label: 'Audit Scheduled' },
    { value: 'AUDIT_IN_PROGRESS', label: 'Audit In Progress' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
  ];

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('auditor:applications.applicationQueue')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('auditor:dashboard.title'), href: '/auditor/dashboard' },
          { label: t('auditor:applications.applicationQueue') },
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">{t('common:app.loading')}</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={t('auditor:applications.applicationQueue')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('auditor:dashboard.title'), href: '/auditor/dashboard' },
        { label: t('auditor:applications.applicationQueue') },
      ]}
    >
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Total Applications</p>
            <p className="text-2xl font-bold text-blue-600">{applications.length}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Under Review</p>
            <p className="text-2xl font-bold text-orange-600">
              {applications.filter(a => a.status === 'UNDER_REVIEW').length}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Scheduled Audits</p>
            <p className="text-2xl font-bold text-purple-600">
              {applications.filter(a => a.status === 'AUDIT_SCHEDULED').length}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Approved</p>
            <p className="text-2xl font-bold text-green-600">
              {applications.filter(a => a.status === 'APPROVED').length}
            </p>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auditor:applications.searchApplications')}
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by company name or type..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auditor:applications.filterByStatus')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Applications Table */}
      <Card>
        <CardBody>
          {filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{t('auditor:applications.noApplications')}</p>
            </div>
          ) : (
            <>
              <Table
                data={paginatedApplications}
                columns={columns}
                keyExtractor={(app) => app.id}
                isLoading={loading}
              />
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </PageLayout>
  );
};
